// Named-parameter scanning + rewriting for the query editor.
//
// Syntax:  :Name  or  :Name#type#   (type optional; drives the input widget).
//   e.g.  UPDATE t SET c='x' WHERE id = :UserID AND d = :tgl#date#
//
// A single pass — quote/comment-aware (mirrors lib/sqlsplit.js) so a colon
// inside a string ('12:30:00'), a backtick, or a comment is NEVER treated as a
// parameter. Returns the distinct params (first occurrence wins the type) and
// the SQL rewritten with `:Name#type#` → `@Name`, so the backend can bind each
// value via `SET @Name = ?` (injection-safe) and the query reads `@Name`.

// :Name or :Name#type#  — name is an identifier, type is letters.
const PARAM_RE = /^:([A-Za-z_][A-Za-z0-9_]*)(?:#([A-Za-z]+)#)?/;

// scanParams(sql, onParam) — single quote/comment-aware pass (mirrors
// lib/sqlsplit.js). For every `:Name[#type#]` token OUTSIDE strings/comments/
// backticks it calls onParam(name, type, token) and appends the returned string;
// everything else (incl. colons inside strings/comments) is copied verbatim.
// Returns the rebuilt SQL. Both extractParams + substituteParams build on this.
function scanParams(sql, onParam) {
  let out = '';
  let inSingle = false, inDouble = false, inBacktick = false;
  let inLine = false, inBlock = false;

  for (let i = 0; i < sql.length; i++) {
    const c = sql[i];
    const nx = sql[i + 1];

    if (inLine)  { out += c; if (c === '\n') inLine = false; continue; }
    if (inBlock) { out += c; if (c === '*' && nx === '/') { out += nx; inBlock = false; i++; } continue; }
    if (inSingle) {
      out += c;
      if (c === '\\' && nx) { out += nx; i++; continue; }
      if (c === "'") inSingle = false;
      continue;
    }
    if (inDouble) {
      out += c;
      if (c === '\\' && nx) { out += nx; i++; continue; }
      if (c === '"') inDouble = false;
      continue;
    }
    if (inBacktick) { out += c; if (c === '`') inBacktick = false; continue; }

    // Entering a comment / string region — copy verbatim, scan no params inside.
    // (A `#type#` suffix never reaches the `#` check: the `:` branch consumes it.)
    if (c === '-' && nx === '-') { out += c + nx; inLine  = true; i++; continue; }
    if (c === '#')               { out += c;      inLine  = true; continue; } // MySQL `#` comment
    if (c === '/' && nx === '*') { out += c + nx; inBlock = true; i++; continue; }
    if (c === "'") { out += c; inSingle   = true; continue; }
    if (c === '"') { out += c; inDouble   = true; continue; }
    if (c === '`') { out += c; inBacktick = true; continue; }

    if (c === ':') {
      const m = PARAM_RE.exec(sql.slice(i));
      if (m) {
        out += onParam(m[1], (m[2] || '').toLowerCase(), m[0]);
        i += m[0].length - 1; // skip the consumed token (loop i++ lands after it)
        continue;
      }
    }

    out += c;
  }
  return out;
}

// extractParams(sql) → { params: [{ name, type }], rewritten: string }
//   params  — distinct by name, in first-seen order; `type` is lower-cased or ''.
//   rewritten — sql with every param token replaced by `@Name`.
export function extractParams(sql) {
  const params = [];
  const seen = new Map(); // name → index in params
  const rewritten = scanParams(sql, (name, type) => {
    if (!seen.has(name)) {
      seen.set(name, params.length);
      params.push({ name, type });
    } else if (type && !params[seen.get(name)].type) {
      params[seen.get(name)].type = type; // upgrade a bare :Name to a typed one
    }
    return '@' + name;
  });
  return { params, rewritten };
}

// Coerce a typed query-parameter value for binding. MySQL coerces strings in
// comparisons, so most types pass through as strings; numbers bind numeric
// (index-friendly), datetime normalises its 'T' to a space, bool → 1/0. An
// integer beyond 2^53 stays its exact digit string: a JS number (and the JSON
// float the server decodes) would round 1234567890123456789 to …800.
export function coerceParam(raw, type) {
  switch (type) {
    case 'int':
    case 'number': {
      const n = Number(raw);
      const t = String(raw ?? '').trim();
      if (/^-?\d+$/.test(t) && !Number.isSafeInteger(n)) return t;
      return raw !== '' && Number.isFinite(n) ? n : String(raw ?? '');
    }
    case 'bool': return (raw === true || raw === '1' || raw === 'true') ? 1 : 0;
    case 'datetime':
    case 'timestamp': return String(raw ?? '').replace('T', ' ');
    default: return String(raw ?? ''); // text / varchar / char / date / …
  }
}

// HTML input type for a param widget (bool is rendered as a <select> instead).
export function paramInputType(type) {
  switch (type) {
    case 'date': return 'date';
    case 'datetime':
    case 'timestamp': return 'datetime-local';
    case 'int':
    case 'number': return 'number';
    default: return 'text';
  }
}

// substituteParams(sql, literals) → sql with each `:Name` token replaced by
// literals[Name] (a ready, pre-escaped SQL literal). Produces a self-contained
// query (values pasted in) rather than the bound `@Name` form. A param with no
// entry in `literals` keeps its original `:Name` token. Same quote/comment-aware
// scan, so colons in strings/comments are never touched.
export function substituteParams(sql, literals) {
  return scanParams(sql, (name, _type, token) =>
    (literals && Object.prototype.hasOwnProperty.call(literals, name)) ? literals[name] : token);
}

// unscopedWriteKind(sql) → 'UPDATE' | 'DELETE' | null.
// Returns the verb when a statement is an UPDATE/DELETE with NO top-level WHERE
// and no top-level LIMIT — i.e. it would touch every row. Quote/comment-aware,
// and paren-depth-aware so a WHERE inside a subquery doesn't count as scoping
// the outer write. A client-side early warning; the server's SQL_SAFE_UPDATES
// is the authoritative guard.
export function unscopedWriteKind(sql) {
  // First keyword (skip leading comments/whitespace), matched on a word boundary.
  const lead = stripLeading(sql);
  let verb = null;
  if (/^update\b/i.test(lead)) verb = 'UPDATE';
  else if (/^delete\b/i.test(lead)) verb = 'DELETE';
  if (!verb) return null;

  let depth = 0;
  let inSingle = false, inDouble = false, inBacktick = false;
  let inLine = false, inBlock = false;
  for (let i = 0; i < sql.length; i++) {
    const c = sql[i], nx = sql[i + 1];
    if (inLine)  { if (c === '\n') inLine = false; continue; }
    if (inBlock) { if (c === '*' && nx === '/') { inBlock = false; i++; } continue; }
    if (inSingle)   { if (c === '\\' && nx) { i++; continue; } if (c === "'") inSingle = false; continue; }
    if (inDouble)   { if (c === '\\' && nx) { i++; continue; } if (c === '"') inDouble = false; continue; }
    if (inBacktick) { if (c === '`') inBacktick = false; continue; }
    if (c === '-' && nx === '-') { inLine  = true; i++; continue; }
    if (c === '#')               { inLine  = true; continue; }
    if (c === '/' && nx === '*') { inBlock = true; i++; continue; }
    if (c === "'") { inSingle = true; continue; }
    if (c === '"') { inDouble = true; continue; }
    if (c === '`') { inBacktick = true; continue; }
    if (c === '(') { depth++; continue; }
    if (c === ')') { if (depth > 0) depth--; continue; }
    // Only top-level keywords scope the write.
    if (depth === 0 && (c === 'W' || c === 'w' || c === 'L' || c === 'l')) {
      const rest = sql.slice(i);
      if (/^where\b/i.test(rest) || /^limit\b/i.test(rest)) return null; // scoped
    }
  }
  return verb; // no top-level WHERE / LIMIT found
}

function stripLeading(sql) {
  let s = sql;
  // Drop leading whitespace + line/block comments so the first keyword is found.
  for (;;) {
    const t = s.replace(/^\s+/, '');
    if (t.startsWith('--') || t.startsWith('#')) { const nl = t.indexOf('\n'); s = nl === -1 ? '' : t.slice(nl + 1); continue; }
    if (t.startsWith('/*')) { const e = t.indexOf('*/'); s = e === -1 ? '' : t.slice(e + 2); continue; }
    return t;
  }
}

// extractWriteTarget(sql) → { kind, table, hasWhere, hasLimit, whereCols }
// Used by the "Lax — key column" safe-update tier (client-side check) and any
// future write-shape introspection. Goes deeper than unscopedWriteKind:
//
//   kind       'UPDATE' | 'DELETE' | null
//   table      target table name, unquoted (db.tbl → tbl); null if unparseable
//   hasWhere   true if a TOP-LEVEL WHERE clause exists
//   hasLimit   true if a TOP-LEVEL LIMIT clause exists
//   whereCols  unique column identifiers referenced in the WHERE clause,
//              table-qualifier stripped (t.id → id), reserved words excluded,
//              function-call names excluded (NOW( is dropped). Lower-cased for
//              case-insensitive matching against the cached key-columns list.
//
// Heuristic — good enough for the "did the WHERE touch a key column" question
// but not a full SQL parser. Subquery columns are included (the user said any
// reference to a key counts under Lax-Key); the strict tier is the real guard
// when correctness matters.
const SQL_WORDS_NOT_COLS = new Set([
  'AND','OR','NOT','IS','NULL','TRUE','FALSE',
  'LIKE','BETWEEN','IN','EXISTS','ALL','ANY','SOME',
  'CASE','WHEN','THEN','ELSE','END',
  'DIV','MOD','XOR','BINARY','COLLATE','INTERVAL','CAST','CONVERT',
  'SELECT','FROM','WHERE','JOIN','INNER','LEFT','RIGHT','FULL','CROSS',
  'ON','USING','AS','DISTINCT','STRAIGHT_JOIN','NATURAL',
  'ORDER','BY','ASC','DESC','GROUP','HAVING',
  'LIMIT','OFFSET','INTO','SET','UPDATE','DELETE',
]);

export function extractWriteTarget(sql) {
  const empty = { kind: null, table: null, hasWhere: false, hasLimit: false, whereCols: [] };
  const lead = stripLeading(sql);
  let kind = null;
  if (/^update\b/i.test(lead)) kind = 'UPDATE';
  else if (/^delete\b/i.test(lead)) kind = 'DELETE';
  if (!kind) return empty;

  const n = sql.length;

  // Skip whitespace + comments starting at p; return the new position.
  const skipWs = (p) => {
    while (p < n) {
      const c = sql[p], nx = sql[p + 1];
      if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { p++; continue; }
      if ((c === '-' && nx === '-') || c === '#') {
        const nl = sql.indexOf('\n', p + 1);
        p = nl === -1 ? n : nl + 1;
        continue;
      }
      if (c === '/' && nx === '*') {
        const e = sql.indexOf('*/', p + 2);
        p = e === -1 ? n : e + 2;
        continue;
      }
      return p;
    }
    return p;
  };

  // Read an identifier (bare or backtick-quoted) at position p; null if none.
  const readIdent = (p) => {
    if (p >= n) return null;
    if (sql[p] === '`') {
      const end = sql.indexOf('`', p + 1);
      if (end === -1) return null;
      return { end: end + 1, name: sql.slice(p + 1, end) };
    }
    const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(sql.slice(p));
    if (!m) return null;
    return { end: p + m[0].length, name: m[0] };
  };

  // Read a (possibly db-qualified) table reference: ident or ident.ident.
  // Returns the FINAL ident (db.tbl → tbl).
  const readTableRef = (p) => {
    const first = readIdent(p);
    if (!first) return null;
    let q = skipWs(first.end);
    if (q < n && sql[q] === '.') {
      q = skipWs(q + 1);
      const second = readIdent(q);
      if (second) return { end: second.end, name: second.name };
    }
    return { end: first.end, name: first.name };
  };

  // ── Phase 1: locate target table + the top-level WHERE + LIMIT bounds.
  // We walk forward once, paren+quote+comment-aware. The verb consumed the
  // first keyword; pick the cursor up right after.
  let pos = skipWs(/^update\b/i.test(lead) ? sql.toLowerCase().indexOf('update') + 6
                                            : sql.toLowerCase().indexOf('delete') + 6);
  let table = null;
  if (kind === 'UPDATE') {
    const ref = readTableRef(pos);
    if (ref) { table = ref.name; pos = ref.end; }
  } else {
    // DELETE [ignored-modifiers]* FROM <table>
    // Scan ahead to find the first top-level "FROM <ident>". (Multi-table
    // DELETE is rare; we take the first table and that's the Lax-Key target.)
    let p = pos;
    while (p < n) {
      const w = readIdent(p);
      if (!w) { p++; continue; }
      if (/^from$/i.test(w.name)) {
        const ref = readTableRef(skipWs(w.end));
        if (ref) { table = ref.name; pos = ref.end; }
        break;
      }
      p = skipWs(w.end);
    }
  }

  // Walk the rest with quote/comment/paren-awareness, looking for top-level
  // WHERE / LIMIT / ORDER tokens.
  let hasWhere = false, hasLimit = false, whereStart = -1, whereEnd = n;
  let depth = 0;
  let inSingle = false, inDouble = false, inBacktick = false;
  let inLine = false, inBlock = false;
  for (let i = pos; i < n; i++) {
    const c = sql[i], nx = sql[i + 1];
    if (inLine)  { if (c === '\n') inLine = false; continue; }
    if (inBlock) { if (c === '*' && nx === '/') { inBlock = false; i++; } continue; }
    if (inSingle)   { if (c === '\\' && nx) { i++; continue; } if (c === "'") inSingle = false; continue; }
    if (inDouble)   { if (c === '\\' && nx) { i++; continue; } if (c === '"') inDouble = false; continue; }
    if (inBacktick) { if (c === '`') inBacktick = false; continue; }
    if (c === '-' && nx === '-') { inLine  = true; i++; continue; }
    if (c === '#')               { inLine  = true; continue; }
    if (c === '/' && nx === '*') { inBlock = true; i++; continue; }
    if (c === "'") { inSingle = true; continue; }
    if (c === '"') { inDouble = true; continue; }
    if (c === '`') { inBacktick = true; continue; }
    if (c === '(') { depth++; continue; }
    if (c === ')') { if (depth > 0) depth--; continue; }
    if (depth !== 0) continue;
    // Top-level: look for WHERE / LIMIT / ORDER as word-boundary tokens.
    if (/[A-Za-z_]/.test(c)) {
      // Match at a word boundary (i.e., previous char is not [A-Za-z0-9_])
      const prev = i > 0 ? sql[i - 1] : ' ';
      if (/[A-Za-z0-9_]/.test(prev)) continue;
      const rest = sql.slice(i);
      if (!hasWhere && /^where\b/i.test(rest)) {
        hasWhere = true;
        whereStart = i + 5;
        // We keep scanning to find where WHERE ends (next top-level ORDER/LIMIT).
      } else if (/^limit\b/i.test(rest)) {
        hasLimit = true;
        if (hasWhere && whereEnd === n) whereEnd = i;
      } else if (/^order\b/i.test(rest)) {
        if (hasWhere && whereEnd === n) whereEnd = i;
      }
    }
  }

  // ── Phase 2: extract column identifiers from the WHERE text.
  // Heuristic: walk the WHERE clause; for each identifier decide column vs
  // table/function/keyword. Distinguishing column refs from table refs in
  // subqueries (`...FROM u WHERE u.x=1`) is the trickiest bit — we track the
  // last non-trivial keyword and skip the ident that follows FROM/JOIN/INTO/
  // UPDATE, since the SQL grammar makes those a table position.
  const TABLE_CONTEXT = new Set(['FROM', 'JOIN', 'INTO', 'UPDATE']);
  const whereCols = [];
  if (hasWhere && whereStart >= 0) {
    const seen = new Set();
    const w = sql.slice(whereStart, whereEnd);
    const wn = w.length;
    let ws = false, wd = false, wb = false, wL = false, wB = false;
    let prevWord = '';
    for (let i = 0; i < wn; i++) {
      const c = w[i], nx = w[i + 1];
      if (wL) { if (c === '\n') wL = false; continue; }
      if (wB) { if (c === '*' && nx === '/') { wB = false; i++; } continue; }
      if (ws) { if (c === '\\' && nx) { i++; continue; } if (c === "'") ws = false; continue; }
      if (wd) { if (c === '\\' && nx) { i++; continue; } if (c === '"') wd = false; continue; }
      if (wb) { if (c === '`') wb = false; continue; }
      if (c === '-' && nx === '-') { wL = true; i++; continue; }
      if (c === '#')               { wL = true; continue; }
      if (c === '/' && nx === '*') { wB = true; i++; continue; }
      if (c === "'") { ws = true; continue; }
      if (c === '"') { wd = true; continue; }
      // Identifier at word boundary
      if (c === '`' || /[A-Za-z_]/.test(c)) {
        const prev = i > 0 ? w[i - 1] : ' ';
        if (/[A-Za-z0-9_]/.test(prev) && c !== '`') continue;
        // Read identifier
        let endP, name;
        if (c === '`') {
          const e = w.indexOf('`', i + 1);
          if (e === -1) break;
          name = w.slice(i + 1, e);
          endP = e + 1;
        } else {
          const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(w.slice(i));
          if (!m) continue;
          name = m[0];
          endP = i + m[0].length;
        }
        // Peek next non-ws / non-comment char.
        let q = endP;
        while (q < wn) {
          const cc = w[q], nn = w[q + 1];
          if (cc === ' ' || cc === '\t' || cc === '\n' || cc === '\r') { q++; continue; }
          if ((cc === '-' && nn === '-') || cc === '#') { const nl = w.indexOf('\n', q + 1); q = nl === -1 ? wn : nl + 1; continue; }
          if (cc === '/' && nn === '*') { const ee = w.indexOf('*/', q + 2); q = ee === -1 ? wn : ee + 2; continue; }
          break;
        }
        const next = q < wn ? w[q] : '';
        i = endP - 1; // advance the outer loop past this identifier (loop's i++ will land at endP)
        // Update prevWord BEFORE deciding — table-context check below reads
        // the previous word, then we record the current one for the next ident.
        const lastWord = prevWord;
        prevWord = c === '`' ? '' : name.toUpperCase(); // backticked names don't count as keywords
        if (next === '(') continue; // function call → not a column
        if (TABLE_CONTEXT.has(lastWord)) {
          // Previous keyword was FROM / JOIN / INTO / UPDATE — this ident is a
          // table reference (possibly db.tbl), not a column.
          if (next === '.') {
            // Consume the dotted form to keep alignment, then carry on.
            let r = q + 1;
            while (r < wn && (w[r] === ' ' || w[r] === '\t' || w[r] === '\n' || w[r] === '\r')) r++;
            const m2 = w[r] === '`'
              ? (() => { const e = w.indexOf('`', r + 1); return e === -1 ? null : { end: e + 1 }; })()
              : (() => { const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(w.slice(r)); return m ? { end: r + m[0].length } : null; })();
            if (m2) i = m2.end - 1;
          }
          continue;
        }
        if (next === '.') {
          // It's a table-qualifier. Consume the dot, read the actual column, push that.
          let r = q + 1;
          while (r < wn && (w[r] === ' ' || w[r] === '\t' || w[r] === '\n' || w[r] === '\r')) r++;
          if (r < wn && (w[r] === '`' || /[A-Za-z_]/.test(w[r]))) {
            let colName, colEnd;
            if (w[r] === '`') {
              const ee = w.indexOf('`', r + 1);
              if (ee === -1) continue;
              colName = w.slice(r + 1, ee);
              colEnd = ee + 1;
            } else {
              const m2 = /^[A-Za-z_][A-Za-z0-9_]*/.exec(w.slice(r));
              if (!m2) continue;
              colName = m2[0];
              colEnd = r + m2[0].length;
            }
            const lc = colName.toLowerCase();
            if (!seen.has(lc) && !SQL_WORDS_NOT_COLS.has(colName.toUpperCase())) {
              seen.add(lc);
              whereCols.push(lc);
            }
            i = colEnd - 1;
          }
          continue;
        }
        const lc = name.toLowerCase();
        // Skip reserved words AND drop backtick-quoted names from the keyword list
        // (a column literally named `where` is fine if backtick-quoted; but our
        // simple `prev === '`'` test above means a backtick-quoted ident always
        // gets here regardless of keyword status — that's the intent).
        if (c !== '`' && SQL_WORDS_NOT_COLS.has(name.toUpperCase())) continue;
        if (seen.has(lc)) continue;
        seen.add(lc);
        whereCols.push(lc);
      }
    }
  }

  return { kind, table, hasWhere, hasLimit, whereCols };
}
